import _ from 'underscore';
import gsap from 'gsap';
import * as THREE from 'three';
import IMOG from '~/lib/imog';
import loadGLTF from '~/lib/three/loadGLTF';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';
import {
  CCDIKSolver,
  CCDIKHelper,
} from 'three/examples/jsm/animation/CCDIKSolver';

const raycaster = new THREE.Raycaster();
raycaster.firstHitOnly = true;

const v1 = new THREE.Vector3();
const v2 = new THREE.Vector3();
const v3 = new THREE.Vector3();
const v4 = new THREE.Vector3();
//
const HELPERS = false;
let STEP_DISTANCE = 1;

const stepOriginHelper = new THREE.Mesh(
  new THREE.SphereGeometry(0.1),
  new THREE.MeshBasicMaterial({
    color: 'blue',
    depthTest: false,
    transparent: true,
    visible: HELPERS,
  })
);
stepOriginHelper.renderOrder = 2;

const stepTargetHelper = new THREE.Mesh(
  new THREE.SphereGeometry(0.1),
  new THREE.MeshBasicMaterial({
    color: 'orange',
    depthTest: false,
    transparent: true,
    visible: HELPERS,
  })
);

stepTargetHelper.renderOrder = 2;

export default IMOG.Component('Spider', {
  options: {
    addTo: null,
  },

  props() {
    return {
      active: true,
      walking: false,
    };
  },

  async setup({ options }) {
    const { scene } = await loadGLTF('/gltf/spider-rig.glb', {
      renderer: this.$renderer,
    });

    // Internal state
    // --------------
    this.iterations = 0;
    this._loaded = false;

    // References
    // ----------
    this.spider = scene.getObjectByName('Spider');
    this.spiderMesh = scene.getObjectByName('SpiderMesh');
    this.spiderMesh.frustumCulled = false;
    this.spiderMesh.material.color.setRGB(0.85, 0.85, 0.85);
    this.spiderMesh.visible = this.props.walking;
    this.bones = {};
    this.spiderMesh.skeleton.bones.forEach((bone, i) => {
      bone._index = i;
      this.bones[bone.userData.name.replace('.', '').replace('.', '')] = bone;
    });
    this.spider.scale.setScalar(0.4);
    this.spider.traverse((obj) => {
      if (obj.isMesh) obj.castShadow = true;
    });
    if (options.addTo) options.addTo.add(this.spider);

    this.bones.Root.position.y += 5;

    // Helpers
    // -------
    this.upRef = stepOriginHelper.clone();
    this.upRef.position.y = 1;
    this.bones.Base.add(this.upRef);

    this.baseTarget = stepOriginHelper.clone();
    this.bones.Root.add(this.baseTarget);

    this.stepTarget = stepOriginHelper.clone();
    this.stepTarget.rotation.copy(this.bones.Base.rotation);
    this.stepTarget.position.copy(this.bones.Base.position);
    this.bones.Root.add(this.stepTarget);

    this.positionTarget = stepOriginHelper.clone();
    this.positionTarget.rotation.copy(this.bones.Base.rotation);
    this.positionTarget.position.copy(this.bones.Base.position);
    this.positionTarget.position.y += 5;
    this.bones.Root.add(this.positionTarget);

    // Inverse Kinematics
    // ------------------
    const iks = [];
    ['L1', 'L2', 'L3', 'R1', 'R2', 'R3'].forEach((key) => {
      const side = key[0];
      const index = key[1];
      iks.push({
        target: this.bones[`Leg${index}Target${side}`]._index,
        effector: this.bones[`Leg${index}End${side}`]._index,
        iteration: 15,
        minAngle: 0.001,

        links: [
          {
            index: this.bones[`Leg${index}Base${side}`]._index,
            limitation: { x: 0, y: 1, z: 0 },
          },

          {
            index: this.bones[`Leg${index}${side}`]._index,
            limitation: { x: 1, y: 0, z: 0 },
          },
          {
            index: this.bones[`Leg${index}Bottom${side}`]._index,
            limitation: { x: side === 'L' ? -1 : 1, y: 0, z: 0 },
          },
        ],
      });
    });

    this.ikSolver = new CCDIKSolver(this.spiderMesh, iks);
    this.helper = new CCDIKHelper(this.spiderMesh, iks);
    if (options.addTo && HELPERS) options.addTo.add(this.helper);

    // Controls
    // --------
    this.control = new TransformControls(
      this.$worldCamera,
      this.$renderer.domElement
    );
    // this.control.setMode('rotate');

    this.control.addEventListener('dragging-changed', (event) => {
      this.$orbitControls.enabled = !event.value;
    });
    this.control.showY = false;

    this.control.attach(this.positionTarget);
    if (options.addTo) options.addTo.add(this.control);

    // Steps
    // -----
    this.stepTargetOrigins = {};
    this.stepTargetProjections = {};
    this.stepTweens = {};

    ['L1', 'L2', 'L3', 'R1', 'R2', 'R3'].forEach((key) => {
      const h = stepOriginHelper.clone();
      this.stepTargetOrigins[key] = h;
      this.stepTarget.add(h);

      const hp = stepTargetHelper.clone();
      this.stepTargetProjections[key] = hp;
      this.stepTarget.add(hp);

      const side = key[0];
      const index = key[1];

      const BottomWorldPosition = this.bones[
        `Leg${index}End${side}`
      ].getWorldPosition(v1);
      const BottomLocalPosition = this.bones.Base.worldToLocal(v1);
      h.position
        .copy(this.bones[`Leg${index}Base${side}`].position)
        .lerp(BottomLocalPosition, 0.52);
      // h.position.y = 10;

      this.stepTweens[key] = {
        active: false,
        t: 0,
        from: new THREE.Vector3(),
        to: new THREE.Vector3(),
        curve: new THREE.QuadraticBezierCurve3(),
      };
    });

    IMOG.inject('spider', this);

    if (this._loadedResolve) this._loadedResolve();
    this._loaded = true;
  },

  methods: {
    setStepTargetProjections() {
      const basePos = this.bones.Base.getWorldPosition(v1);
      const upRefPos = this.upRef.getWorldPosition(v2);
      const direction = v3.copy(upRefPos).sub(basePos).normalize();

      _.each(this.stepTargetOrigins, (val, key) => {
        const origin = val.getWorldPosition(v1);
        raycaster.set(origin, direction);
        const intersects = raycaster.intersectObjects(this.$solids, false);
        const intersect = intersects.length ? intersects[0] : null;
        if (intersect) {
          this.stepTargetProjections[key].parent.worldToLocal(intersect.point);
          this.stepTargetProjections[key].position.copy(intersect.point);
          this.stepTargetProjections[key]._normal = intersect.face.normal;
          this.stepTargetProjections[key]._locked = false;
        } else {
          this.stepTargetProjections[key]._locked = true;
        }
      });
    },
    setBaseTarget() {
      // average of two middle legs
      const left = this.bones[`Leg${2}TargetL`].getWorldPosition(v1);
      const right = this.bones[`Leg${2}TargetR`].getWorldPosition(v2);
      const mid = v3.copy(left).lerp(right, 0.5);

      const castOrigin = v1.copy(mid);
      castOrigin.y += 10;

      const dir = v2.set(0, -1, 0);
      raycaster.set(castOrigin, dir);

      const intersects = raycaster.intersectObjects(this.$solids, false);
      const intersect = intersects.length ? intersects[0] : null;
      if (intersect) {
        mid.y -= intersect.distance - 11.1;
      } else {
        mid.y += 1.5;
      }

      // push to target
      const stepTarget = this.stepTarget.getWorldPosition(v4);
      stepTarget.y = mid.y;

      // apply
      if (stepTarget.distanceTo(mid) > 1.4) mid.lerp(stepTarget, 0.22);

      this.baseTarget.parent.worldToLocal(mid);
      this.baseTarget.position.copy(mid);
    },
    setStepTarget() {
      const MAX_STEP_DISTANCE = 1.8;
      const basePosition = this.bones.Base.getWorldPosition(v1);
      const targetPosition = this.positionTarget.getWorldPosition(v2);
      // targetPosition.y = basePosition.y; // TODO ??
      const diff = v3.copy(targetPosition).sub(basePosition);
      if (diff.length() > MAX_STEP_DISTANCE) {
        diff.setLength(MAX_STEP_DISTANCE);
        diff.y += 1;

        const stepTarget = basePosition.add(diff);
        const castOrigin = v4.copy(stepTarget);
        stepTarget.y += 1;

        const dir = v2.set(0, -1, 0);
        raycaster.set(castOrigin, dir);

        const intersects = raycaster.intersectObjects(this.$solids, false);
        const intersect = intersects.length ? intersects[0] : null;
        if (intersect) {
          this.stepTarget.parent.worldToLocal(stepTarget);
          this.stepTarget.position.copy(stepTarget);
          // this.stepTarget.position.y = basePosition.y - 5;
        }
      }
    },
    checkSteps() {
      if (Object.values(this.stepTargetProjections).find((t) => t._locked))
        return;

      const basePosition = this.bones.Base.getWorldPosition(v1);
      const targetPosition = this.positionTarget.getWorldPosition(v2);
      targetPosition.y = basePosition.y;
      const d = targetPosition.distanceTo(basePosition);
      STEP_DISTANCE = d > 1 ? 1.1 : 0.4;
      _.each(this.stepTargetProjections, (targetProjection, key) => {
        const side = key[0];
        const index = key[1];
        const currentPosition = this.bones[
          `Leg${index}End${side}`
        ].getWorldPosition(v1);
        const stepTargetPosition = targetProjection.getWorldPosition(v2);
        const d = v1.distanceTo(v2);
        if (d > STEP_DISTANCE && !this.stepTweens[key].active) {
          const adjecentTweens = this.getAdjecentLegKeys(key).map(
            (key) => this.stepTweens[key]
          );
          const adjecentActive = adjecentTweens.reduce((memo, tween) => {
            return memo || (tween.active && tween.t < 0.9);
          }, false);
          const mirrorTween = this.stepTweens[
            `${side === 'L' ? 'R' : 'L'}${index}`
          ];
          const mirrorActive = mirrorTween.active && mirrorTween.t < 0.9;
          if (!adjecentActive && !mirrorActive) {
            this.triggerStep({
              key,
              from: currentPosition,
              to: stepTargetPosition,
            });
          }
        }
      });
    },
    triggerStep({ key, from, to }) {
      const tween = this.stepTweens[key];
      tween.active = true;
      tween.t = 0;
      tween.from.copy(from);
      const d = v3.copy(to).sub(from).setLength(STEP_DISTANCE);
      tween.to.copy(from).add(d);
      tween.curve.v0.copy(tween.from);
      tween.curve.v2.copy(tween.to);
      tween.curve.v1.copy(tween.from).lerp(tween.curve.v2, 0.5);
      tween.curve.v1.y += 1.25; // TODO: This should be in the direction of the projection normal
      gsap.to(tween, {
        t: 1,
        duration: STEP_DISTANCE > 1 ? 0.17 : 0.33,
        ease: 'power1.out',
        onUpdate: () => this.updateStepTween({ key, tween }),
        onComplete: () => {
          tween.active = false;
          tween.t = 0;
        },
      });
    },
    updateStepTween({ key, tween }) {
      const side = key[0];
      const index = key[1];
      const bone = this.bones[`Leg${index}Target${side}`];
      const p = tween.curve.getPointAt(tween.t);
      bone.parent.worldToLocal(p);
      bone.position.copy(p);
    },
    getAdjecentLegKeys(key) {
      const side = key[0];
      const index = +key[1];
      let sideIndexes = [];
      if (index === 1) sideIndexes = [2];
      if (index === 2) sideIndexes = [1, 3];
      if (index === 3) sideIndexes = [2];
      return sideIndexes.map((index) => `${side}${index}`);
    },
    loaded() {
      return new Promise((resolve) => {
        if (this._loaded) {
          resolve();
          return;
        }
        this._loadedResolve = resolve;
      });
    },
  },

  hooks: {
    'while:active'() {
      if (this.iterations >= 1) this.iterations++;
      if (this.iterations > 30) this.props.walking = true;

      if (this.ikSolver) {
        this.setStepTarget();

        this.setStepTargetProjections();
        this.setBaseTarget();
        this.bones.Base.position.lerp(this.baseTarget.position, 0.2);
        this.bones.Head.position.y = 0.3 * Math.sin(performance.now() * 0.003);

        this.ikSolver.update();
      }
    },
    'while:walking'() {
      this.setStepTarget();
      this.checkSteps();
    },
    'set:walking'(v) {
      if (this.spiderMesh) this.spiderMesh.visible = v;
    },
    'set:active'(active) {
      if (!active) return;

      this.setStepTargetProjections();

      setTimeout(() => {
        _.each(this.stepTargetProjections, (val, key) => {
          const side = key[0];
          const index = key[1];
          const stepWorldPosition = val.getWorldPosition(
            this.bones[`Leg${index}Target${side}`].position
          );
          this.bones.Root.worldToLocal(stepWorldPosition);
        });
        this.setBaseTarget();
        this.iterations = 1;
      }, 1);
    },
  },
});
